/**
 * ClientForm - Unified client create/edit form
 *
 * Supports two variants:
 * - dialog: Used in ClientSheet with collapsible sections, external submit button
 * - inline: Used in EditFormContainer with flat layout, built-in submit/cancel
 *
 * Data can be provided via:
 * - client: ClientRow (snake_case database format)
 * - initialData: ClientFormInitialData (camelCase enriched format)
 */

import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { broadcastInvalidation } from '@/lib/sync'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { ChevronDown } from 'lucide-react'
import {
  clientFormSchema,
  type ClientFormInput,
  type ClientFormValues,
} from './client-form-schema'
import {
  normalizeClientData,
  PREFOCUS_FIELD_MAP,
  type ClientFormInitialData,
} from './client-form-utils'
import type { ClientRow } from '@/types/clients'
import {
  CLIENT_TYPES,
  CLIENT_TYPE_LABELS,
  CLIENT_STATUSES,
  CLIENT_STATUS_LABELS,
  PAYMENT_TERMS_OPTIONS,
  PAYMENT_TERMS_LABELS,
} from '@/types/clients'
import { updateClient } from '@/lib/api/clients'

interface ClientFormProps {
  /** Existing client data (snake_case database format) for edit mode */
  client?: ClientRow | null
  /** Existing client data (camelCase enriched format) for edit mode */
  initialData?: ClientFormInitialData | null
  /** Form variant - affects layout and button placement */
  variant?: 'dialog' | 'inline'
  /** Accordion mode - only one section open at a time (dialog variant only) */
  accordion?: boolean
  /** Field to auto-focus on mount (camelCase or snake_case) */
  prefocusField?: string
  /** Form ID for external submit button binding (dialog variant) */
  formId?: string
  /** Client ID for built-in mutation (inline variant, edit mode) */
  clientId?: string
  /** Called when form is submitted with valid data (external handling) */
  onSubmit?: (data: ClientFormValues) => void | Promise<void>
  /** Called after successful mutation (inline variant) */
  onSuccess?: () => void
  /** Called when cancel button clicked (inline variant) */
  onCancel?: () => void
  /** External loading state (dialog variant) */
  isLoading?: boolean
}

export function ClientForm({
  client,
  initialData,
  variant = 'dialog',
  accordion = false,
  prefocusField,
  formId = 'client-form',
  clientId,
  onSubmit,
  onSuccess,
  onCancel,
  isLoading: externalLoading,
}: ClientFormProps) {
  const queryClient = useQueryClient()
  const isInline = variant === 'inline'

  // Determine initial collapsed state based on existing data
  const hasContact = client?.email_primary || client?.phone_primary || initialData?.emailPrimary || initialData?.phonePrimary
  const hasAddress = client?.address_line1 || initialData?.addressLine1
  const hasBilling = client?.tax_id || initialData?.taxId || client?.tax_exempt || initialData?.taxExempt
  const [detailsOpen, setDetailsOpen] = useState(true)
  const [contactOpen, setContactOpen] = useState(!!hasContact)
  const [addressOpen, setAddressOpen] = useState(!!hasAddress)
  const [billingOpen, setBillingOpen] = useState(!!hasBilling)

  // Accordion: only one section open at a time
  const handleSectionToggle = (section: string, open: boolean) => {
    if (accordion && open) {
      setDetailsOpen(section === 'details')
      setContactOpen(section === 'contact')
      setAddressOpen(section === 'address')
      setBillingOpen(section === 'billing')
    } else {
      const setters: Record<string, (v: boolean) => void> = {
        details: setDetailsOpen,
        contact: setContactOpen,
        address: setAddressOpen,
        billing: setBillingOpen,
      }
      setters[section]?.(open)
    }
  }

  // Initialize form with normalized data
  const form = useForm<ClientFormInput, unknown, ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: normalizeClientData(client, initialData),
  })

  // Re-sync form when async client data arrives (first edit click)
  useEffect(() => {
    if (client || initialData) {
      form.reset(normalizeClientData(client, initialData))
    }
  }, [client, initialData, form])

  // Watch client_type to show/hide name fields conditionally
  const clientType = useWatch({ control: form.control, name: 'client_type' })
  const isCompanyType =
    clientType === 'corporate' ||
    clientType === 'government' ||
    clientType === 'non_profit'

  // Auto-focus the specified field
  useEffect(() => {
    if (prefocusField) {
      const fieldName = PREFOCUS_FIELD_MAP[prefocusField] || prefocusField
      if (fieldName && fieldName in form.getValues()) {
        setTimeout(() => form.setFocus(fieldName as keyof ClientFormInput), 100)
      }
    }
  }, [prefocusField, form])

  // Built-in mutation for inline edit mode
  const mutation = useMutation({
    mutationFn: async (values: ClientFormValues) => {
      if (!clientId) throw new Error('clientId required for mutation')
      return updateClient(clientId, {
        client_type: values.client_type,
        first_name: values.first_name || null,
        last_name: values.last_name || null,
        company_name: values.company_name || null,
        legal_entity_name: values.legal_entity_name || null,
        email_primary: values.email_primary || null,
        phone_primary: values.phone_primary || null,
        phone_secondary: values.phone_secondary || null,
        address_line1: values.address_line1 || null,
        address_line2: values.address_line2 || null,
        city: values.city || null,
        state_province: values.state_province || null,
        postal_code: values.postal_code || null,
        country: values.country || null,
        payment_terms: values.payment_terms,
        tax_exempt: values.tax_exempt,
        tax_id: values.tax_id || null,
        status: values.status ?? 'active',
        notes: values.notes || null,
      })
    },
    onSuccess: () => {
      toast.success('Client updated')
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['client-registry'] })
      queryClient.invalidateQueries({ queryKey: ['allocations'] })
      broadcastInvalidation([['clients'], ['client-registry'], ['allocations']])
      onSuccess?.()
    },
    onError: (error) => {
      toast.error('Failed to update: ' + (error as Error).message)
    },
  })

  // Handle form submission
  const handleSubmit = async (data: ClientFormValues) => {
    if (onSubmit) {
      // External handling (dialog mode)
      await onSubmit(data)
    } else if (clientId) {
      // Built-in mutation (inline edit mode)
      mutation.mutate(data)
    }
  }

  const isSubmitting = externalLoading || mutation.isPending

  // Render client details fields (type, status, name)
  const renderDetailsFields = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="client_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Client Type *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CLIENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {CLIENT_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CLIENT_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {CLIENT_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {isCompanyType ? (
        <>
          <FormField
            control={form.control}
            name="company_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Aviation LLC" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="legal_entity_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Legal Entity Name</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Aviation Holdings Inc." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name *</FormLabel>
                <FormControl>
                  <Input placeholder="John" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name *</FormLabel>
                <FormControl>
                  <Input placeholder="Smith" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </>
  )

  // Render contact fields
  const renderContactFields = () => (
    <>
      <FormField
        control={form.control}
        name="email_primary"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="phone_primary"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Primary Phone</FormLabel>
              <FormControl>
                <Input placeholder="(602) 555-1234" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone_secondary"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Secondary Phone</FormLabel>
              <FormControl>
                <Input placeholder="(602) 555-5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  )

  // Render address fields
  const renderAddressFields = () => (
    <>
      <FormField
        control={form.control}
        name="address_line1"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Address Line 1</FormLabel>
            <FormControl>
              <Input placeholder="123 Aviation Way" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="address_line2"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Address Line 2</FormLabel>
            <FormControl>
              <Input placeholder="Suite 200" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input placeholder="Phoenix" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="state_province"
          render={({ field }) => (
            <FormItem>
              <FormLabel>State/Province</FormLabel>
              <FormControl>
                <Input placeholder="AZ" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="postal_code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Postal Code</FormLabel>
              <FormControl>
                <Input placeholder="85001" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <FormControl>
                <Input placeholder="USA" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  )

  // Render billing fields
  const renderBillingFields = () => (
    <>
      <FormField
        control={form.control}
        name="payment_terms"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Payment Terms</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select payment terms" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {PAYMENT_TERMS_OPTIONS.map((term) => (
                  <SelectItem key={term} value={term}>
                    {PAYMENT_TERMS_LABELS[term]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-2 gap-4 items-end">
        <FormField
          control={form.control}
          name="tax_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tax ID / EIN</FormLabel>
              <FormControl>
                <Input placeholder="12-3456789" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tax_exempt"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0 pb-2">
              {isInline ? (
                <>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Tax Exempt {field.value ? '(Yes)' : '(No)'}
                  </FormLabel>
                </>
              ) : (
                <>
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">Tax Exempt</FormLabel>
                </>
              )}
            </FormItem>
          )}
        />
      </div>
    </>
  )

  return (
    <Form {...form}>
      <form
        id={isInline ? undefined : formId}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        {/* Contact, Address & Billing - layout depends on variant */}
        {isInline ? (
          // Inline variant: flat layout with separators and styled headers
          <>
            <div className="space-y-4">
              <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Client Details</h3>
              {renderDetailsFields()}
            </div>
            <Separator />
            <div className="space-y-4">
              <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Contact Information</h3>
              {renderContactFields()}
            </div>
            <Separator />
            <div className="space-y-4">
              <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Address</h3>
              {renderAddressFields()}
            </div>
            <Separator />
            <div className="space-y-4">
              <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Billing</h3>
              {renderBillingFields()}
            </div>
          </>
        ) : (
          // Dialog variant: collapsible sections with styled headers
          <>
            <Collapsible open={detailsOpen} onOpenChange={(open) => handleSectionToggle('details', open)}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" type="button" className="flex w-full justify-between bg-muted/50 hover:bg-muted px-2 py-1.5 h-auto rounded-md">
                  <span className="text-sm font-medium">Client Details</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2 pl-3 border-l-2 border-muted ml-1">
                {renderDetailsFields()}
              </CollapsibleContent>
            </Collapsible>

            <Separator />

            <Collapsible open={contactOpen} onOpenChange={(open) => handleSectionToggle('contact', open)}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" type="button" className="flex w-full justify-between bg-muted/50 hover:bg-muted px-2 py-1.5 h-auto rounded-md">
                  <span className="text-sm font-medium">Contact Information</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${contactOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2 pl-3 border-l-2 border-muted ml-1">
                {renderContactFields()}
              </CollapsibleContent>
            </Collapsible>

            <Separator />

            <Collapsible open={addressOpen} onOpenChange={(open) => handleSectionToggle('address', open)}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" type="button" className="flex w-full justify-between bg-muted/50 hover:bg-muted px-2 py-1.5 h-auto rounded-md">
                  <span className="text-sm font-medium">Address</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${addressOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2 pl-3 border-l-2 border-muted ml-1">
                {renderAddressFields()}
              </CollapsibleContent>
            </Collapsible>

            <Separator />

            <Collapsible open={billingOpen} onOpenChange={(open) => handleSectionToggle('billing', open)}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" type="button" className="flex w-full justify-between bg-muted/50 hover:bg-muted px-2 py-1.5 h-auto rounded-md">
                  <span className="text-sm font-medium">Billing Information</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${billingOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2 pl-3 border-l-2 border-muted ml-1">
                {renderBillingFields()}
              </CollapsibleContent>
            </Collapsible>
          </>
        )}

        <Separator />

        {/* Notes */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium bg-muted/50 px-2 py-1.5 rounded-md">Notes</h3>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Internal notes about this client..."
                    className={isInline ? 'min-h-[60px] resize-none' : 'min-h-[80px]'}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Inline variant: include action buttons */}
        {isInline && (
          <div className="flex justify-end gap-2 pt-2 border-t">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}
